import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DrillThroughTablesPanel } from "@/components/DrillThroughTablesPanel";
import { DrillThroughPageShell } from "@/components/DrillThroughPageShell";
import { MultiSelect } from "@/components/GenericFilterBar";
import { useToast } from "@/hooks/use-toast";
import { useDrillThroughTable } from "@/hooks/useDrillThroughTable";
import { useSidebarPersistence } from "@/hooks/useSidebarPersistence";
import { getDrillFiltersFromSearch } from "@/lib/drillThroughTableQuery";
import {
  createValueColumns,
  formatMonthLabel as formatDrillMonthLabel,
  getSelectedMonth as getDrillSelectedMonth,
  normalizeAgeBands as normalizeDrillAgeBands,
} from "@/lib/drillThroughTableNormalizers";
import type { SortState } from "@/lib/tableSort";
import {
  downloadDebtorsDrillThroughExcel,
  fetchDebtorsCurrentDrillThrough,
  fetchDebtorsFilterOptions,
  DEBTORS_DRILL_FILTER_KEYS,
  type DebtorFilters,
  type DebtorsClientAssignmentGroup,
  type DebtorsDrillThroughResponse,
  type DebtorsDrillThroughRow,
} from "@/analytics/debtors.api";
import type { DrillColumn, DrillThroughTableConfig } from "@/types/drillThroughTable";

type ExportOption = "assignment_manager" | "client_assignment";

const LOADING_MESSAGE = "Loading Debtors Current drill-through...";

const getManagerRows = (response: DebtorsDrillThroughResponse) =>
  response.by_assignment_manager?.length ? response.by_assignment_manager : response.by_manager;
const getManagerTotal = (response: DebtorsDrillThroughResponse) => response.assignment_manager_total_rows;
const getClientRows = (response: DebtorsDrillThroughResponse) => response.by_client_assignment;
const getClientTotal = (response: DebtorsDrillThroughResponse) => response.client_assignment_total_rows;

export default function DebtorsDrillThroughTablesPage() {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const selectedMonth = getDrillSelectedMonth(searchParams);
  const searchKey = searchParams.toString();

  const [serviceLines, setServiceLines] = useState<string[]>(() => searchParams.getAll("service_lines"));
  const [serviceLineOptions, setServiceLineOptions] = useState<string[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    fetchDebtorsFilterOptions(undefined, controller.signal)
      .then((options) => setServiceLineOptions(options.service_lines))
      .catch(() => {});
    return () => controller.abort();
  }, []);

  const toggleServiceLine = (value: string) => {
    setServiceLines((current) =>
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
    );
  };

  // Base filters only -- sort is owned independently per table below, never merged in here.
  const drillFilters = useMemo(
    () => ({
      ...getDrillFiltersFromSearch(searchParams, [...DEBTORS_DRILL_FILTER_KEYS]),
      service_lines: serviceLines,
    }),
    [searchParams, serviceLines],
  );

  const buildManagerFetchFilters = useCallback(
    (sort: SortState) => ({ ...drillFilters, managerSort: sort }),
    [drillFilters],
  );
  const buildClientFetchFilters = useCallback(
    (sort: SortState) => ({ ...drillFilters, clientSort: sort }),
    [drillFilters],
  );

  const managerTable = useDrillThroughTable<DebtorsDrillThroughResponse, DebtorsDrillThroughRow>({
    selectedMonth,
    searchKey,
    drillFilters,
    getRows: getManagerRows,
    getTotal: getManagerTotal,
    fetchPage: fetchDebtorsCurrentDrillThrough,
    buildFetchFilters: buildManagerFetchFilters,
    errorMessage: "Unable to load Debtors Current drill-through data",
  });
  const clientTable = useDrillThroughTable<DebtorsDrillThroughResponse, DebtorsClientAssignmentGroup>({
    selectedMonth,
    searchKey,
    drillFilters,
    getRows: getClientRows,
    getTotal: getClientTotal,
    fetchPage: fetchDebtorsCurrentDrillThrough,
    buildFetchFilters: buildClientFetchFilters,
    errorMessage: "Unable to load Debtors Current drill-through data",
  });

  const [isExporting, setIsExporting] = useState(false);
  const [exportOptions, setExportOptions] = useState<ExportOption[]>([]);
  const [sidebarOpen, onSidebarOpenChange] = useSidebarPersistence();

  const handleExport = async () => {
    if (!selectedMonth || isExporting || exportOptions.length === 0) return;

    setIsExporting(true);
    try {
      await downloadDebtorsDrillThroughExcel(
        selectedMonth,
        drillFilters as Partial<DebtorFilters>,
        exportOptions,
      );
    } catch (caught) {
      toast({
        variant: "destructive",
        title: "Couldn't export Debtors drill-through",
        description:
          caught instanceof Error ? caught.message : "Failed to export Excel file",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const toggleExportOption = (option: ExportOption) => {
    setExportOptions((current) =>
      current.includes(option)
        ? current.filter((value) => value !== option)
        : [...current, option],
    );
  };

  const ageBands = useMemo(
    () => normalizeDrillAgeBands(managerTable.response?.age_bands ?? clientTable.response?.age_bands),
    [managerTable.response, clientTable.response],
  );

  const managerColumns = useMemo<DrillColumn[]>(
    () => [
      { key: "manager", label: "Manager", kind: "text", sortKey: "name", sortType: "text" },
      ...createValueColumns(ageBands),
    ],
    [ageBands],
  );
  const clientColumns = useMemo<DrillColumn[]>(
    () => [
      { key: "client_name", label: "Client Name", kind: "text", sortKey: "name", sortType: "text" },
      { key: "assignment_name", label: "Assignment Name", kind: "text" },
      ...createValueColumns(ageBands),
    ],
    [ageBands],
  );

  const tables = useMemo<DrillThroughTableConfig[]>(
    () => [
      {
        title: "Manager / Age Band Summary",
        rows: managerTable.rows,
        columns: managerColumns,
        totalRows: managerTable.totalRows,
        hasMore: managerTable.hasMore,
        loading: managerTable.loading,
        refetching: managerTable.refetching,
        loadingMessage: LOADING_MESSAGE,
        error: managerTable.error,
        loadingMore: managerTable.loadingMore,
        onLoadMore: managerTable.loadMore,
        sort: managerTable.sort,
        onSort: managerTable.onSort,
      },
      {
        title: "Client Assignment / Age Band Summary",
        rows: clientTable.rows,
        columns: clientColumns,
        groupByClient: true,
        countNoun: "clients",
        totalRows: clientTable.totalRows,
        hasMore: clientTable.hasMore,
        loading: clientTable.loading,
        refetching: clientTable.refetching,
        loadingMessage: LOADING_MESSAGE,
        error: clientTable.error,
        loadingMore: clientTable.loadingMore,
        onLoadMore: clientTable.loadMore,
        sort: clientTable.sort,
        onSort: clientTable.onSort,
      },
    ],
    [managerColumns, clientColumns, managerTable, clientTable],
  );

  const monthLabel =
    managerTable.response?.monthLabel ||
    clientTable.response?.monthLabel ||
    formatDrillMonthLabel(selectedMonth) ||
    "Selected month";

  return (
    <DrillThroughPageShell
      title="Debtors Current Drill Through"
      subtitle={monthLabel}
      backLabel="Back to Debtors"
      sidebarOpen={sidebarOpen}
      onSidebarOpenChange={onSidebarOpenChange}
      exportOptions={[
        { label: "Assignment Manager", value: "assignment_manager" },
        { label: "Client Assignment", value: "client_assignment" },
      ]}
      selectedExportOptions={exportOptions}
      onToggleExportOption={toggleExportOption}
      isExporting={isExporting}
      onExport={handleExport}
      error={!selectedMonth ? managerTable.error ?? clientTable.error : null}
      onRetry={() => {
        managerTable.reload();
        clientTable.reload();
      }}
      filterSlot={
        <MultiSelect
          label="Service Line"
          options={[...new Set([...serviceLineOptions, ...serviceLines])]}
          availableOptions={serviceLineOptions}
          selected={serviceLines}
          onToggle={toggleServiceLine}
        />
      }
    >
      <DrillThroughTablesPanel tables={tables} />
    </DrillThroughPageShell>
  );
}
